var _a, _b, _c, _d, _e, _f, _g, _h, _j;
import { __decorate, __metadata, __param } from "tslib";
import { AbstractSpaceActionService } from '@/services/space-action-service';
import { RavenService } from '@workspace/shared/modules/raven-module/raven-service';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { EditSpaceService } from '@/domains/spaces/services/edit-space-service.interface';
import { Logger } from '@patsnap/tahiti-core/common/logger';
import { SpacesModel } from '@/domains/spaces/model/spaces-model';
import { I18nService } from '@/services/i18n-service';
import { Message } from '@patsnap/tahiti-core/common/message';
import { SpaceLabelService } from '@/domains/spaces/services/space-label-service.interface';
import { EditingSpaceModel } from '@/domains/editing-space/models/editing-space-model.interface';
import { SpaceTagsModel } from '@/domains/space-tag/model/space-tags-model';
/**
 * Represents a service that requests space edit data.
 */
export const RequestSpaceEditDataService = Symbol('RequestSpaceEditDataService');
let EditSpaceContribution = class EditSpaceContribution extends AbstractSpaceActionService {
    constructor(_ravenService, _editSpaceService, _requestSpaceEditDataService, _logger, _message, _spacesModel, _i18nService, _spaceLabelService, _editingSpaceModel, _spaceTagsModel) {
        super();
        this._ravenService = _ravenService;
        this._editSpaceService = _editSpaceService;
        this._requestSpaceEditDataService = _requestSpaceEditDataService;
        this._logger = _logger;
        this._message = _message;
        this._spacesModel = _spacesModel;
        this._i18nService = _i18nService;
        this._spaceLabelService = _spaceLabelService;
        this._editingSpaceModel = _editingSpaceModel;
        this._spaceTagsModel = _spaceTagsModel;
        this._requestSpaceEditDataService.onReceived(this._onReceived.bind(this));
        this._requestSpaceEditDataService.onAborted(this._onAborted.bind(this));
    }
    async _onReceived(data, next) {
        const currentSpace = this.currentSpaceService.getCurrentSpace();
        let updatedSpace = {
            ...data,
            spaceId: currentSpace.spaceId,
        };
        /* If the icon is not changed, we should not send the iconImageUrl to the backend. */
        if (updatedSpace.iconImageUrl && (updatedSpace.iconImageUrl === currentSpace.iconImageUrl)) {
            updatedSpace = {
                ...updatedSpace,
                iconImageUrl: undefined,
                buildInIconId: undefined,
            };
        }
        const tags = this._spaceTagsModel.modelData.map(tag => tag.tagId);
        updatedSpace.tags = updatedSpace.tags.filter(tagId => tags.includes(tagId));
        const resp = await this._editSpaceService.editSpace(updatedSpace);
        this._ravenService.track('Update Workspace');
        this._spacesModel.update(this.updateSpace(resp));
        this._message.success({
            message: this._i18nService.t('workspace.labels.editOk'),
            showClose: true,
        });
        this._spaceLabelService.resetSpaceLabel();
        next();
    }
    _change(space, editableSpaceData) {
        if (space.spaceId === editableSpaceData.spaceId) {
            return {
                ...space,
                ...editableSpaceData,
            };
        }
        return space;
    }
    updateSpace(editableSpaceData) {
        return (spaces) => {
            return spaces.map(space => this._change(space, editableSpaceData));
        };
    }
    _onAborted() {
        this._logger.log('Edit Space Dialog Aborted');
    }
    async execute() {
        this._ravenService.track('Click Edit Workspace');
        this._editingSpaceModel.update(() => {
            return {
                ...this.currentSpaceService.getCurrentSpace(),
            };
        });
        return this._requestSpaceEditDataService.request({
            title: this._i18nService.t('workspace.labels.editWorkspace'),
        }).finally(() => {
            this._editingSpaceModel.dispose();
        });
    }
    registerCommands(commandRegistry) {
        commandRegistry.registerCommand({
            id: 'edit-space',
        }, this);
    }
    registerSpaceActions(spaceActionRegistry) {
        spaceActionRegistry.registerSpaceAction({
            order: 20,
            commandId: 'edit-space',
            label: this._i18nService.t('global.btn.edit'),
            iconClass: 'SolidEdit',
            isEnabled: (space) => {
                if (space.isPublic) {
                    return space.isManager;
                }
                return !space.isShared;
            },
            isVisible: (space) => {
                return !space.isShared;
            },
        });
    }
};
EditSpaceContribution = __decorate([
    injectable(),
    __param(0, inject(RavenService)),
    __param(1, inject(EditSpaceService)),
    __param(2, inject(RequestSpaceEditDataService)),
    __param(3, inject(Logger)),
    __param(4, inject(Message)),
    __param(5, inject(SpacesModel)),
    __param(6, inject(I18nService)),
    __param(7, inject(SpaceLabelService)),
    __param(8, inject(EditingSpaceModel)),
    __param(9, inject(SpaceTagsModel)),
    __metadata("design:paramtypes", [typeof (_a = typeof RavenService !== "undefined" && RavenService) === "function" ? _a : Object, typeof (_b = typeof EditSpaceService !== "undefined" && EditSpaceService) === "function" ? _b : Object, Object, typeof (_c = typeof Logger !== "undefined" && Logger) === "function" ? _c : Object, typeof (_d = typeof Message !== "undefined" && Message) === "function" ? _d : Object, typeof (_e = typeof SpacesModel !== "undefined" && SpacesModel) === "function" ? _e : Object, typeof (_f = typeof I18nService !== "undefined" && I18nService) === "function" ? _f : Object, typeof (_g = typeof SpaceLabelService !== "undefined" && SpaceLabelService) === "function" ? _g : Object, typeof (_h = typeof EditingSpaceModel !== "undefined" && EditingSpaceModel) === "function" ? _h : Object, typeof (_j = typeof SpaceTagsModel !== "undefined" && SpaceTagsModel) === "function" ? _j : Object])
], EditSpaceContribution);
export { EditSpaceContribution };
